import { defineStore } from 'pinia'
import { normalize, schema } from 'normalizr'
import Vue from 'vue'
import { updateStoreState } from '../helpers/stateUtils'
import {
  useAlertStore,
  useUnitStore,
  useUnitCalendarFeedStore,
  useBookingStore,
  useBookingRuleStore,
  useBookableStore,
} from './'
import i18n from '../plugins/i18n'

const booking = new schema.Entity('bookings')
const calendarFeed = new schema.Entity('calendar_feeds')
const unit = new schema.Entity('units', {
  bookings: [booking],
  calendarFeeds: [calendarFeed],
})
const bookable = new schema.Entity('bookables', {
  units: [unit],
})

const bookingRule = new schema.Entity('booking_rules')

const channel = new schema.Entity('booking_channels')

const place = new schema.Entity('places', {
  bookables: [bookable],
  rollingBookingRule: bookingRule,
  seasonalBookingRules: [bookingRule],
  bookingChannels: [channel],
})

export const usePlaceStore = defineStore({
  id: 'place',
  state: () => ({
    places: [],
  }),
  getters: {
    findById: (state) => (id) => state.places.find((place) => place.id === id),
    findByEzObjectId: (state) => (id) => state.places.find((place) => place.ezObjectId === id),
    readyToPublish: (state) => (id) => {
      const bookableStore = useBookableStore()
      const unitStore = useUnitStore()
      const place = state.findById(id)
      const bookables = bookableStore.findByIds(place.bookables)

      let ready = true

      if (!bookables.length) {
        ready = false
      }

      // loop over each bookables units and check the ready flags
      bookables.forEach((bookable) => {
        const units = unitStore.findByIds(bookable.units)
        if (!units.length) ready = false

        const hasUnreadyUnits = units.find((unit) => unit.ready !== true)
        if (hasUnreadyUnits) {
          ready = false
        }
      })

      return ready
    },
  },
  actions: {
    httpGetPlace ({ placeId }) {
      const bookableStore = useBookableStore()
      const unitStore = useUnitStore()
      const unitCalendarFeedStore = useUnitCalendarFeedStore()
      const bookingStore = useBookingStore()
      const bookingRuleStore = useBookingRuleStore()
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ
        .get(`/place/${placeId}`)
        .then((response) => {
          const { entities } = normalize(response.data, place)

          if (entities.places) {
            // remove extras and rates as they are handled through their own loaders
            const places = Object.values(entities.places).map(place => {
              place.extras = []
              place.rates = []
              return place
            })

            updateStoreState(this.places, places)
          }
          if (entities.bookables) {
            updateStoreState(bookableStore.bookables, Object.values(entities.bookables))
          }
          if (entities.units) {
            // unset extras and rates as they are handled through their own loaders
            const units = Object.values(entities.units).map(unit => {
              unit.extras = []
              unit.rates = []
              return unit
            })

            updateStoreState(unitStore.units, units)
          }
          if (entities.calendar_feeds) {
            updateStoreState(unitCalendarFeedStore.feeds, Object.values(entities.calendar_feeds))
          }
          if (entities.bookings) {
            updateStoreState(bookingStore.bookings, Object.values(entities.bookings))
          }
          if (entities.booking_rules) {
            updateStoreState(bookingRuleStore.rules, Object.values(entities.booking_rules))
          }
        })
        .catch((error) => {
          alertStore.setAlertError({ message: i18n.t('page-place.actions.load.toast-error') })
          throw error
        })
    },

    httpUpdatePlace ({ place }) {
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ
        .post(`/place/${place.id}`, place)
        .then((response) => {
          updateStoreState(this.places, place)

          alertStore.setAlertSuccess({ message: i18n.t('page-place.actions.update.toast-success') })

          return response
        })
        .catch(() => {
          alertStore.setAlertError({ message: i18n.t('page-place.actions.update.toast-error') })
        })
    },
  },
})
